import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  TablePagination,
  TextField,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Divider
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Tooltip from '@mui/material/Tooltip';
import { toast } from 'react-toastify';
import NumberFormatCustom from '../../globals/utils/Bond Creation Utils/numberFormatCustom';
import Navbar from '../../globals/components/Navbar';
import { apiClient } from '../../globals/utils/api';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import { setPgBondsFilters, setPgBondsPage, resetPgBondsState } from '../../store/slices/pgBondsSlice';
import TablePaginationActions from "../../globals/components/TablePaginationActions";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const statusOptions = ["Active", "Rejected", "To Be Cancelled", "Cancelled"];

const ENDORSEMENT_NAME_CHANGE_TYPES = ["Name_Change_Principal", "Name_Change_County", "Name_Change_Deceased_Ward_Beneficiary"];
const isNameChangeEndorsement = (t) => ENDORSEMENT_NAME_CHANGE_TYPES.includes(t);

const DWB_LABEL_MAP = {
  Probate: { prefix: "Deceased Legal", alias: "Alias (Deceased)" },
  Guardianship: { prefix: "Ward", alias: "Ward Alias" },
  VA: { prefix: "Beneficiary", alias: "Beneficiary Alias" },
  Trustee: { prefix: "Full Legal Name", single: true },
};

function ProfessionalGuardianViewBonds() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [searchQuery] = useState(''); // eslint-disable-line no-unused-vars
  const savedPage = useSelector((state) => state.pgBonds?.page ?? 0);
  const [page, setPage] = useState(savedPage);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [allBonds, setAllBonds] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const { id } = useParams();
  const { role } = useSelector((state) => state.auth);
  const savedFilters = useSelector((state) => state.pgBonds?.filters) || {
    bondNumber: [],
    bondType: [],
    bondSubCategory: [],
    principal: [],
    estate: [],
    lawFirm: [],
    attorney: [],
    internalUser: [],
    status: [],
    startDate: null,
    endDate: null,
  };

  const userdetails = useSelector((state) => state.auth.user);


  // Filter states
  const [filters, setFilters] = useState(savedFilters);

  // Options for filters
  const [bondTypes, setBondTypes] = useState([]);
  const [lawFirms, setLawFirms] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [internalUsers, setInternalUsers] = useState([]);
  const [estates, setEstates] = useState([]);
  const [principals, setPrincipals] = useState([]);

  // Cancellation states
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedBondToCancel, setSelectedBondToCancel] = useState(null);
  const [cancellationFile, setCancellationFile] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Endorsement states
  const [endorsementDialogOpen, setEndorsementDialogOpen] = useState(false);
  const [selectedBondToEndorse, setSelectedBondToEndorse] = useState(null);
  const [endorsementType, setEndorsementType] = useState("Increase");
  const [newBondAmount, setNewBondAmount] = useState("");
  const [endorsementFile, setEndorsementFile] = useState(null);
  const [isSubmittingEndorsement, setIsSubmittingEndorsement] = useState(false);
  const [contactName, setContactName] = useState(userdetails.first_name + " " + userdetails.last_name);
  const [contactEmail, setContactEmail] = useState(userdetails.email);
  const [updatedName, setUpdatedName] = useState("");
  const [counties, setCounties] = useState([]);
  const [endorsementToCounty, setEndorsementToCounty] = useState("");
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [estimatedAssets, setEstimatedAssets] = useState("");
  const [dwbFirstName, setDwbFirstName] = useState("");
  const [dwbMiddleName, setDwbMiddleName] = useState("");
  const [dwbLastName, setDwbLastName] = useState("");
  const [dwbSuffix, setDwbSuffix] = useState("");
  const [dwbAlias, setDwbAlias] = useState("");

  const bondTypeToSubcategories = {
    Probate: [
      "Administrator",
      "Conservator",
      "Curator",
      "Executor",
      "Personal Representative",
    ],
    Guardianship: [
      "Guardian - Incapacitated",
      "Guardian - Minor",
      "Guardian - Advocate",
      "Conservator",
    ],
    Blanket: ["1 Year Bond", "2 Year Bond", "3 Year Bond"],
  };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (filters.lawFirm.length > 0) {
      const firmIds = filters.lawFirm.map((f) => f.value);
      Promise.all(
        firmIds.map((id) => apiClient(`/lawfirm-user-location/${id}`, "GET"))
      ).then((responses) => {
        const merged = [].concat(...responses)
          .filter((a) => a.Position === "Attorney")
          .map((a) => ({
            label: a.lawfirm_user_name,
            value: a.lawfirm_user_id,
          }));
        setAttorneys(merged);
      });
    } else {
      setAttorneys([]);
    }
  }, [filters.lawFirm]);

  const fetchInitialData = async (overrideFilters = null) => {
    if (!id) return;
    setLoading(true);
    try {
      // Use list-guardian-bonds as the single source
      const guardianBondsData = await apiClient(`/list-guardian-bonds/${id}/`, 'GET');
      const rawList = Array.isArray(guardianBondsData) ? guardianBondsData : [];

      const filteredBonds = rawList.map((bond) => ({
        bond_id: bond.bond_id,
        bond_number: bond.bond_number || '',
        bond_type: bond.bond_type,
        bond_sub_category: bond.bond_sub_category || '',
        principals: Array.isArray(bond.principals) ? bond.principals : [],
        estate_name: bond.name,
        law_firm_name: bond.law_firm_name,
        attorney_name: bond.law_firm_user_name,
        underwriting_status: bond.underwriting_status || '-',
        bond_amount: bond.bond_amount || '0.00',
        managed_by: (bond.managed_by || []).map((u) => (typeof u === 'object' && u?.full_name ? u.full_name : u)),
        status: bond.status || 'Active',
        created_at: bond.created_at,
        principal_one: Array.isArray(bond.principals) ? bond.principals[0] : '',
      }));
      
      // Fetch filter options (bond types and law firms only; internal users only for internal/super)
      const [bt, lf] = await Promise.all([
        apiClient("/bondtypes/", "GET"),
        apiClient("/list-lawfirms/", "GET")
      ]);

      setBondTypes(
        bt
          .filter((b) => b.type !== "Guardian-Minor")
          .map((b) => ({ label: b.type, value: b.type }))
      );
      
      // Extract distinct law firm names from filtered bonds
      const distinctLawFirmNames = [...new Set(
        filteredBonds.map(bond => bond.law_firm_name).filter(Boolean)
      )];
      
      // Filter law firms to only include those that exist in guardian's bonds
      const filteredLawFirms = lf.filter(firm => 
        distinctLawFirmNames.includes(firm.name)
      ).map(f => ({ label: f.name, value: f.lawfirm_id }));
      
      setLawFirms(filteredLawFirms);

      // Managed By filter: only fetch internal users for internal_user / super_user (no API call for other roles)
      if (role === 'internal_user' || role === 'super_user') {
        const iu = await apiClient("/internal-user", "GET");
        setInternalUsers(
          (iu?.data || []).map((u) => ({ label: u.full_name, value: u.internal_user_id }))
        );
      } else {
        setInternalUsers([]);
      }

      // Extract distinct principals from filtered bonds
      const distinctPrincipals = [...new Set(
        filteredBonds.flatMap(bond => bond.principals)
      )].filter(Boolean).map((principal, index) => ({
        label: principal,
        bondType: filteredBonds.find(bond => bond.principals.includes(principal))?.bond_type || '',
        value: `${principal}-${filteredBonds.find(bond => bond.principals.includes(principal))?.bond_type || ''}-${index}`
      }));

      // Extract distinct estates from filtered bonds
      const distinctEstates = [...new Set(
        filteredBonds.map(bond => bond.estate_name).filter(Boolean)
      )].map((estate, index) => ({
        label: estate,
        bondType: filteredBonds.find(bond => bond.estate_name === estate)?.bond_type || '',
        value: `${estate}-${filteredBonds.find(bond => bond.estate_name === estate)?.bond_type || ''}-${index}`
      }));

      setPrincipals(distinctPrincipals);
      setEstates(distinctEstates);

      const reversed = [...filteredBonds].reverse();
      setAllBonds(reversed);

      // Re-apply filters (allow override on manual refresh)
      let result = [...reversed];
      const f = overrideFilters ?? filters ?? savedFilters;

      if (f.bondNumber?.length)
        result = result.filter((b) => f.bondNumber.some((x) => b.bond_number === x.value));

      if (f.startDate) {
        const start = dayjs(f.startDate).startOf('day');
        result = result.filter((b) => dayjs(b.created_at).isSameOrAfter(start, 'day'));
      }

      if (f.endDate) {
        const end = dayjs(f.endDate).endOf('day');
        result = result.filter((b) => dayjs(b.created_at).isSameOrBefore(end, 'day'));
      }

      if (f.bondType?.length)
        result = result.filter((b) => f.bondType.some((x) => x.value === b.bond_type));

      if (f.bondSubCategory?.length)
        result = result.filter((b) => f.bondSubCategory.includes(b.bond_sub_category));

      if (f.principal?.length)
        result = result.filter((b) => f.principal.some((x) => b.principals.includes(x.label)));

      if (f.estate?.length)
        result = result.filter((b) => f.estate.some((x) => b.estate_name === x.label));

      if (f.lawFirm?.length)
        result = result.filter((b) => f.lawFirm.some((x) => b.law_firm_name === x.label));

      if (f.attorney?.length)
        result = result.filter((b) => f.attorney.some((x) => b.attorney_name === x.label));

      if (f.internalUser?.length && (role === 'internal_user' || role === 'super_user'))
        result = result.filter((b) => f.internalUser.some((x) => b.managed_by.includes(x.label)));

      if (f.status?.length)
        result = result.filter((b) => f.status.includes(b.status));

      setFilteredData(result);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching bonds:", error);
      setLoading(false);
    }
  };

  // Sort function
  const sortRows = (rows) => {
    return [...rows].sort((a, b) => {
      if (orderBy === 'bond_amount') {
        const valA = parseFloat(a.bond_amount);
        const valB = parseFloat(b.bond_amount);
        return order === 'asc' ? valA - valB : valB - valA;
      } else if (orderBy === 'created_at') {
        return order === 'asc'
          ? new Date(a.created_at) - new Date(b.created_at)
          : new Date(b.created_at) - new Date(a.created_at);
      } else {
        const valA = a[orderBy]?.toString().toLowerCase() || '';
        const valB = b[orderBy]?.toString().toLowerCase() || '';
        return order === 'asc'
          ? valA.localeCompare(valB)
          : valB.localeCompare(valA);
      }
    });
  };

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const applyFilters = () => {
    let result = [...allBonds];

    if (filters.bondNumber.length)
      result = result.filter((b) =>
        filters.bondNumber.some((f) => b.bond_number === f.value)
      );

    if (filters.startDate) {
      const start = dayjs(filters.startDate).startOf("day");
      result = result.filter((b) =>
        dayjs(b.created_at).isSameOrAfter(start, "day")
      );
    }

    if (filters.endDate) {
      const end = dayjs(filters.endDate).endOf("day");
      result = result.filter((b) =>
        dayjs(b.created_at).isSameOrBefore(end, "day")
      );
    }

    if (filters.bondType.length)
      result = result.filter((b) =>
        filters.bondType.some((f) => f.value === b.bond_type)
      );

    if (filters.bondSubCategory.length)
      result = result.filter((b) =>
        filters.bondSubCategory.includes(b.bond_sub_category)
      );

    if (filters.principal.length)
      result = result.filter((b) =>
        filters.principal.some((f) => b.principals.includes(f.label))
      );

    if (filters.estate.length)
      result = result.filter((b) =>
        filters.estate.some((f) => b.estate_name === f.label)
      );

    if (filters.lawFirm.length)
      result = result.filter((b) =>
        filters.lawFirm.some((f) => b.law_firm_name === f.label)
      );

    if (filters.attorney.length)
      result = result.filter((b) =>
        filters.attorney.some((f) => b.attorney_name === f.label)
      );

    if (filters.internalUser.length && (role === 'internal_user' || role === 'super_user'))
      result = result.filter((b) =>
        filters.internalUser.some((f) => b.managed_by.includes(f.label))
      );

    if (filters.status.length)
      result = result.filter((b) => {
        // If "Active" is selected, include both "Active" and "Approved" bonds
        if (filters.status.includes("Active")) {
          const otherStatuses = filters.status.filter(s => s !== "Active");
          return b.status === "Active" || b.status === "Approved" || otherStatuses.includes(b.status);
        }
        return filters.status.includes(b.status);
      });

    setFilteredData(result);
    setFilterDialogOpen(false);
    setPage(0);
    dispatch(setPgBondsFilters(filters));
    dispatch(setPgBondsPage(0));
  };

  const clearFilters = () => {
    const cleared = {
      bondNumber: [],
      bondType: [],
      bondSubCategory: [],
      principal: [],
      estate: [],
      lawFirm: [],
      attorney: [],
      internalUser: [],
      status: [],
      startDate: null,
      endDate: null,
    };
    setFilters(cleared);
    dispatch(setPgBondsFilters(cleared));
    setPage(0);
    dispatch(setPgBondsPage(0));
  };


  // Format bond amount with dollar sign and commas
  const formatBondAmount = (amount) => {
    if (amount === null) return '$0.00';
    return `$${Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Transform status display - show "Approved" as "Active"
  const transformStatusForDisplay = (status) => {
    return status === 'Approved' ? 'Active' : status;
  };

  // Get cancellation instructions based on bond type and amount
  // eslint-disable-next-line no-unused-vars
  const getCancellationInstructions = (bond) => {
    const amount = parseFloat(bond.bond_amount || 0);
    const type = bond.bond_type;

    if (type === "Probate") {
      if (amount <= 250000) {
        return "Probate\n\nBonds up to $250,000\n\n• Discharge from the court\n\n• Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs";
      }
      return "Probate\n\nBonds over $250,000 require one of the following to close:\n\n• Final Accounting\n\n• Discharge from the court\n\n• Copy of verification of closing evidence from a court's online system";
    }

    if (type === "Guardianship") {
      const instructions =
        amount <= 1000000
          ? "Guardianship\n\nBonds up to $1,000,000 also accept one of the following to close:\n\n• Final Accounting\n\n• Discharge from the court\n\n• Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs and/or beneficiaries"
          : "Guardianship\n\nBonds over $1,000,000 require one of the following to close:\n\n• Final Accounting\n\n• Discharge from the court\n\n• Copy of verification of closing evidence from a court's online system";
      const successorNote =
        "\n\nIf a Successor Bond has been filed with the court:\n\n• Guardianship - Evidence of death of the incompetent\n\n• Minor Guardian - Evidence of the minor reaching the age of majority";
      return instructions + successorNote;
    }

    if (type === "VA") {
      return "VA Bonds\n\nExoneration Letter from the VA";
    }

    if (type === "Trustee") {
      return "Trustee Bond\n\n• Court's Release\n\n• Final Accounting\n\n• Evidence that either the beneficiary of trust has died or that trust assets have been fully dispersed to beneficiary(s) per the Trust";
    }

    if (type === "Blanket") {
      return "📩 Upload email confirmation that renewal is not required.";
    }

    return "⚠️ Upload appropriate closing document for this bond.";
  };

  const getCancellationInstructionsList = (bond) => {
    const amount = parseFloat(bond.bond_amount || 0);
    const type = bond.bond_type;
    if (type === "Probate") {
      if (amount <= 250000) {
        return [
          "Discharge from the court",
          "Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs",
        ];
      }
      return [
        "Final Accounting",
        "Discharge from the court",
        "Copy of verification of closing evidence from a court's online system",
      ];
    }
    if (type === "Guardianship") {
      const main =
        amount <= 1000000
          ? [
              "Final Accounting",
              "Discharge from the court",
              "Statement from attorney on their letterhead stating all debts are paid and balances distributed to the remaining heirs and/or beneficiaries",
            ]
          : [
              "Final Accounting",
              "Discharge from the court",
              "Copy of verification of closing evidence from a court's online system",
            ];
      return [
        ...main,
        "If a Successor Bond has been filed with the court:",
        "Guardianship - Evidence of death of the incompetent",
        "Minor Guardian - Evidence of the minor reaching the age of majority",
      ];
    }
    if (type === "VA") return ["Exoneration Letter from the VA"];
    if (type === "Trustee") {
      return [
        "Court's Release",
        "Final Accounting",
        "Evidence that either the beneficiary of trust has died or that trust assets have been fully dispersed to beneficiary(s) per the Trust",
      ];
    }
    if (type === "Blanket") return ["Email confirmation that renewal is not required."];
    return ["Upload appropriate closing document for this bond."];
  };

  // Get endorsement instructions based on bond type
  const getEndorsementInstructions = (bond) => {
    const type = bond.bond_type;

    const typeSupports = {
      courtOrder: ["Probate", "Guardianship", "Trustee"],
      accounting: ["Guardianship", "Trustee"],
      vaLetter: ["VA"],
    };

    return [
      ...(typeSupports.courtOrder.includes(type)
        ? ["• Court Order or Endorsement Checklist"]
        : []),
      ...(typeSupports.accounting.includes(type)
        ? ["• Annual Accounting Documentation"]
        : []),
      ...(typeSupports.vaLetter.includes(type)
        ? ["• VA Letter of Endorsement"]
        : []),
    ];
  };

  const bondStateId = selectedBondToEndorse?.state ?? 6;
  useEffect(() => {
    if (!endorsementDialogOpen || !bondStateId) return;
    const fetchCounties = async () => {
      setLoadingCounties(true);
      try {
        const countyResponse = await apiClient(
          `/counties-by-state/${bondStateId}/`,
          "GET"
        );
        const formatted = (countyResponse || []).map((c) => ({
          label: c.bond_county,
          value: c.bond_county,
        }));
        setCounties(formatted);
      } catch {
        setCounties([]);
      } finally {
        setLoadingCounties(false);
      }
    };
    fetchCounties();
  }, [endorsementDialogOpen, bondStateId]);

  // Apply search filter to filtered data
  const searchFilteredData = filteredData.filter((bond) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      bond.principals.some((p) => p.toLowerCase().includes(query)) ||
      bond.bond_number?.toLowerCase().includes(query) ||
      bond.estate_name?.toLowerCase().includes(query) ||
      bond.law_firm_name?.toLowerCase().includes(query) ||
      bond.attorney_name?.toLowerCase().includes(query) ||
      ((role === 'internal_user' || role === 'super_user') && bond.managed_by.some((m) => m.toLowerCase().includes(query))) ||
      transformStatusForDisplay(bond.status)?.toLowerCase().includes(query)
    );
  });

  // Apply sorting to search filtered data
  const sortedData = sortRows(searchFilteredData);
  const currentRows = sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    dispatch(setPgBondsPage(newPage));
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    dispatch(setPgBondsPage(0));
  };

  const tableColumns = [
    { key: 'bond_number', label: 'Bond Number' },
    { key: 'bond_type', label: 'Bond Type' },
    { key: 'bond_sub_category', label: 'Bond Sub Category' },
    { key: 'principals', label: 'Principals' },
    { key: 'estate_name', label: 'Estate Name' },
    { key: 'law_firm_name', label: 'Law Firm' },
    { key: 'attorney_name', label: 'Attorney' },
    ...(role === 'internal_user' || role === 'super_user' ? [{ key: 'managed_by', label: 'Managed By' }] : []),
    { key: 'bond_amount', label: 'Bond Amount' },
    { key: 'status', label: 'Status' },
    { key: 'created_at', label: 'Created At' }
  ];

  const selectedBondTypes = filters.bondType.map((b) => b.value);

  const availableSubcategories = selectedBondTypes.flatMap(
    (type) => bondTypeToSubcategories[type] || []
  );

  const availablePrincipals = selectedBondTypes.length
    ? principals.filter((p) => selectedBondTypes.includes(p.bondType))
    : principals;

  const availableEstates = selectedBondTypes.length
    ? estates.filter((e) => selectedBondTypes.includes(e.bondType))
    : estates;

  return (
    <>
      <Navbar />
      <Box sx={{ maxWidth: '100%', padding: '20px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
            Professional Guardian Bonds
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            {/* Search bar */}
            <Button
              onClick={() => {
                const cleared = {
                  bondNumber: [],
                  bondType: [],
                  bondSubCategory: [],
                  principal: [],
                  estate: [],
                  lawFirm: [],
                  attorney: [],
                  internalUser: [],
                  status: [],
                  startDate: null,
                  endDate: null,
                };
                setFilters(cleared);
                setPage(0);
                dispatch(resetPgBondsState());
                fetchInitialData(cleared);
              }}
              startIcon={<RefreshIcon />}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>
            <Button
              variant="contained"
              onClick={() => setFilterDialogOpen(true)}
              startIcon={<FilterListIcon />}
            >
              Filters
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ borderRadius: '8px' }}>
            <Table>
              <TableHead>
                <TableRow style={{ backgroundColor: '#f0f0f0' }}>
                  {tableColumns.map((col) => (
                    <TableCell
                      key={col.key}
                      sortDirection={orderBy === col.key ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === col.key}
                        direction={orderBy === col.key ? order : 'asc'}
                        onClick={() => handleSort(col.key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody sx={{ '& tr:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                {searchFilteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length + 1} align="center">
                      <Typography variant="h6" sx={{ textAlign: 'center' }}>
                        No Data Available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentRows.map((bond, index) => (
                    <TableRow key={index}>
                      <TableCell>{bond.bond_number}</TableCell>
                      <TableCell>{bond.bond_type}</TableCell>
                      <TableCell>{bond.bond_sub_category}</TableCell>
                      <TableCell>{bond.principals.join(', ')}</TableCell>
                      <TableCell>{bond.estate_name}</TableCell>
                      <TableCell>{bond.law_firm_name}</TableCell>
                      <TableCell>{bond.attorney_name}</TableCell>
                      {(role === 'internal_user' || role === 'super_user') && (
                        <TableCell>{bond.managed_by.join(', ')}</TableCell>
                      )}
                      <TableCell>{formatBondAmount(bond.bond_amount)}</TableCell>
                      <TableCell>{transformStatusForDisplay(bond.status)}</TableCell>
                      <TableCell>{dayjs(bond.created_at).format('DD MMM YYYY')}</TableCell>
                      <TableCell align="center">
                        <Tooltip title="View Bond Details">
                          <IconButton
                            color="primary"
                            onClick={() => {
                              let url = '';
                              
                              if (bond.bond_type === 'VA') {
                                url = `/vermost-va-bond-update/${bond.bond_id}`;
                              } else if (bond.bond_type === 'Trustee') {
                                url = `/vermost-trustee-bond-update/${bond.bond_id}`;
                              } else if (bond.bond_type === 'Probate') {
                                url = `/vermost-probate-bond-update/${bond.bond_id}`;
                              } else if (bond.bond_type === 'Blanket') {
                                url = `/vermost-blanket-bond-update/${bond.bond_id}`;
                              } else {
                                url = `/vermost-guardianship-bond-update/${bond.bond_id}`;
                              }
                              
                              if (url) {
                                window.open(url, '_blank');
                              }
                            }}                
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel Bond">
                          <IconButton
                            color="error"
                            onClick={() => {
                              setSelectedBondToCancel(bond);
                              setCancellationFile(null);
                              setCancelDialogOpen(true);
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Bond Endorsement">
                          <IconButton
                            color="secondary"
                            onClick={() => {
                              setSelectedBondToEndorse(bond);
                              setEndorsementType("Increase");
                              setNewBondAmount("");
                              setEndorsementFile(null);
                            setUpdatedName("");
                              setEndorsementToCounty("");
                              setEstimatedAssets("");
                              setDwbFirstName("");
                              setDwbMiddleName("");
                              setDwbLastName("");
                              setDwbSuffix("");
                              setDwbAlias("");
                              setEndorsementDialogOpen(true);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        <TablePagination
          ActionsComponent={TablePaginationActions}
          component="div"
          count={searchFilteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{ mt: 2 }}
        />

        {/* Filter Dialog */}
        <Dialog
          open={filterDialogOpen}
          onClose={() => setFilterDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Apply Filters</DialogTitle>
          <DialogContent
            sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
          >
            <Autocomplete
              multiple
              options={bondTypes}
              value={filters.bondType}
              onChange={(e, v) => setFilters((f) => ({ ...f, bondType: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Bond Type" />}
            />
            <Autocomplete
              multiple
              options={availableSubcategories}
              value={filters.bondSubCategory}
              onChange={(e, v) =>
                setFilters((f) => ({ ...f, bondSubCategory: v }))
              }
              renderInput={(p) => (
                <TextField {...p} label="Bond Sub Category" />
              )}
            />
            <Autocomplete
              multiple
              options={[
                ...new Set(allBonds.map((b) => b.bond_number).filter(Boolean)),
              ].map((num) => ({ label: num, value: num }))}
              value={filters.bondNumber}
              onChange={(e, v) => setFilters((f) => ({ ...f, bondNumber: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Bond Number" />}
            />

            <Autocomplete
              multiple
              options={availablePrincipals}
              value={filters.principal}
              onChange={(e, v) => setFilters((f) => ({ ...f, principal: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Principal" />}
            />
            <Autocomplete
              multiple
              options={availableEstates}
              value={filters.estate}
              onChange={(e, v) => setFilters((f) => ({ ...f, estate: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Estate / Ward / Trust / Beneficiary" />}
            />
            <Autocomplete
              multiple
              options={lawFirms}
              value={filters.lawFirm}
              onChange={(e, v) => setFilters((f) => ({ ...f, lawFirm: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Law Firm" />}
            />
            <Autocomplete
              multiple
              options={attorneys}
              value={filters.attorney}
              onChange={(e, v) => setFilters((f) => ({ ...f, attorney: v }))}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderOption={(props, option) => (
                <li {...props} key={option.value}>
                  {option.label}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Attorney" />}
            />
            {(role === 'internal_user' || role === 'super_user') && (
              <Autocomplete
                multiple
                options={internalUsers}
                value={filters.internalUser}
                onChange={(e, v) =>
                  setFilters((f) => ({ ...f, internalUser: v }))
                }
                getOptionLabel={(o) => o.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderOption={(props, option) => (
                  <li {...props} key={option.value}>
                    {option.label}
                  </li>
                )}
                renderInput={(p) => <TextField {...p} label="Managed By" />}
              />
            )}
            <Autocomplete
              multiple
              options={statusOptions}
              value={filters.status}
              onChange={(e, v) => setFilters((f) => ({ ...f, status: v }))}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  {option}
                </li>
              )}
              renderInput={(p) => <TextField {...p} label="Status" />}
            />
            <TextField
              type="date"
              label="Start Date"
              value={filters.startDate || ""}
              onChange={(e) =>
                setFilters((f) => ({ ...f, startDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              type="date"
              label="End Date"
              value={filters.endDate || ""}
              onChange={(e) =>
                setFilters((f) => ({ ...f, endDate: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={clearFilters}>Clear</Button>
            <Button onClick={() => setFilterDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={applyFilters}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>

        {/* Cancellation Dialog */}
        <Dialog
          open={cancelDialogOpen}
          onClose={() => !isCancelling && setCancelDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: "1.3rem" }}>Bond Cancellation</DialogTitle>
          <DialogContent dividers>
            {selectedBondToCancel && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Cancellation Details
                </Typography>
                <Typography sx={{ mb: 1 }}>
                  <strong>Bond Number:</strong> {selectedBondToCancel.bond_number ?? "N/A"}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  <strong>Bond Type:</strong> {selectedBondToCancel.bond_type}
                </Typography>

                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Documents
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                  Acceptable Documentation:
                </Typography>
                <ul style={{ paddingLeft: "24px", marginBottom: "20px", listStyleType: "disc" }}>
                  {getCancellationInstructionsList(selectedBondToCancel).map(
                    (item, idx) => (
                      <li
                        key={idx}
                        style={{ fontSize: "0.9rem", marginBottom: "6px" }}
                      >
                        {item}
                      </li>
                    )
                  )}
                </ul>

                <Button
                  variant="outlined"
                  component="label"
                  disabled={isCancelling}
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      const validTypes = [
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      ];
                      if (file && !validTypes.includes(file.type)) {
                        toast.error("Only PDF or Word documents are allowed.");
                        e.target.value = "";
                        return;
                      }
                      setCancellationFile(file);
                      toast.success("File selected successfully!");
                    }}
                  />
                </Button>
                {cancellationFile && (
                  <Typography variant="caption" sx={{ display: "block", mb: 2 }}>
                    Selected File: {cancellationFile.name}
                  </Typography>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ padding: 2 }}>
            <Button onClick={() => setCancelDialogOpen(false)} disabled={isCancelling}>
              Close
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!cancellationFile || isCancelling}
              onClick={async () => {
                if (!cancellationFile || !selectedBondToCancel) return;
                setIsCancelling(true);
                const formData = new FormData();
                formData.append("cancellation_file", cancellationFile);
                try {
                  await apiClient(
                    `/cancel-bond/`,
                    "POST",
                    formData,
                    { bond_id: selectedBondToCancel.bond_id },
                    { "Content-Type": "multipart/form-data" }
                  );
                  toast.success("Bond cancelled successfully!");
                  setCancelDialogOpen(false);
                  await fetchInitialData();
                } catch (error) {
                  console.error(error);
                  toast.error(error.message || "Failed to cancel the bond.");
                } finally {
                  setIsCancelling(false);
                }
              }}
            >
              {isCancelling ? <CircularProgress size={24} color="inherit" /> : "Submit Cancellation"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Endorsement Dialog */}
        <Dialog
          open={endorsementDialogOpen}
          onClose={() =>
            !isSubmittingEndorsement && setEndorsementDialogOpen(false)
          }
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: '1.3rem' }}>Bond Endorsement</DialogTitle>
          <DialogContent dividers>
            {selectedBondToEndorse?.bond_type === "Blanket" ? (
              <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
                Endorsement not allowed for Blanket.
              </Typography>
            ) : selectedBondToEndorse ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Endorsement Details
                </Typography>
                <Typography sx={{ mb: 1 }}>
                  <strong>Bond Number:</strong> {selectedBondToEndorse.bond_number ?? "N/A"}
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  <strong>Bond Type:</strong> {selectedBondToEndorse.bond_type}
                </Typography>

                <TextField
                  label="Endorsement Type"
                  select
                  fullWidth
                  value={endorsementType}
                  onChange={(e) => setEndorsementType(e.target.value)}
                  SelectProps={{ native: true }}
                  required
                  sx={{ mb: 2 }}
                >
                  <option value="Increase">Increase</option>
                  <option value="Decrease">Decrease</option>
                  <option value="Name_Change_Principal">Name change (Principal)</option>
                  <option value="Name_Change_County">Name change (County)</option>
                  <option value="Name_Change_Deceased_Ward_Beneficiary">Name change (Deceased/Ward/Beneficiary)</option>
                </TextField>

                {isNameChangeEndorsement(endorsementType) ? (
                  endorsementType === "Name_Change_County" ? (
                    <Autocomplete
                      options={counties}
                      loading={loadingCounties}
                      value={
                        endorsementToCounty
                          ? counties.find((c) => c.value === endorsementToCounty) || null
                          : null
                      }
                      onChange={(e, selected) =>
                        setEndorsementToCounty(selected ? selected.value : "")
                      }
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option.label
                      }
                      isOptionEqualToValue={(option, value) =>
                        option && value && option.value === value.value
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="To County"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingCounties ? (
                                  <CircularProgress color="inherit" size={18} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      fullWidth
                      disabled={loadingCounties}
                      sx={{ mb: 3 }}
                    />
                  ) : endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? (
                    (() => {
                      const labels = DWB_LABEL_MAP[selectedBondToEndorse?.bond_type] || DWB_LABEL_MAP.Probate;
                      if (labels.single) {
                        return (
                          <TextField
                            label={labels.prefix}
                            value={updatedName}
                            onChange={(e) => setUpdatedName(e.target.value)}
                            fullWidth
                            required
                            sx={{ mb: 3 }}
                          />
                        );
                      }
                      return (
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField label={`${labels.prefix} First Name`} value={dwbFirstName} onChange={(e) => setDwbFirstName(e.target.value)} fullWidth required />
                            <TextField label={`${labels.prefix} Middle Name`} value={dwbMiddleName} onChange={(e) => setDwbMiddleName(e.target.value)} fullWidth />
                          </Box>
                          <Box sx={{ display: "flex", gap: 2 }}>
                            <TextField label={`${labels.prefix} Last Name`} value={dwbLastName} onChange={(e) => setDwbLastName(e.target.value)} fullWidth required />
                            <TextField label="Suffix" value={dwbSuffix} onChange={(e) => setDwbSuffix(e.target.value)} sx={{ maxWidth: 120 }} />
                          </Box>
                          <TextField label={labels.alias} value={dwbAlias} onChange={(e) => setDwbAlias(e.target.value)} fullWidth />
                        </Box>
                      );
                    })()
                  ) : (
                    <TextField
                      label="Updated Name"
                      name="updatedName"
                      value={updatedName}
                      onChange={(e) => setUpdatedName(e.target.value)}
                      fullWidth
                      required
                      sx={{ mb: 3 }}
                    />
                  )
                ) : (
                  <TextField
                    label="New Bond Amount (Rounded Up)"
                    name="newBondAmount"
                    value={newBondAmount}
                    onChange={(e) => setNewBondAmount(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      inputComponent: NumberFormatCustom,
                    }}
                    sx={{ mb: 3 }}
                  />
                )}

                {(selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && (
                  <TextField
                    label="Updated Estimated Assets (optional)"
                    type="text"
                    inputMode="decimal"
                    value={estimatedAssets}
                    onChange={(e) => setEstimatedAssets(e.target.value)}
                    fullWidth
                    placeholder="e.g. 250000"
                    sx={{ mb: 2 }}
                    helperText="Fill the updated estimated assets value if additional assets were discovered."
                  />
                )}

                <Divider sx={{ mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Documents
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1 }}>
                  Acceptable Documentation:
                </Typography>
                <ul style={{ paddingLeft: "20px", marginBottom: "20px" }}>
                  {getEndorsementInstructions(selectedBondToEndorse).map(
                    (item, idx) => (
                      <li
                        key={idx}
                        style={{ fontSize: "0.9rem", marginBottom: "6px" }}
                      >
                        {item}
                      </li>
                    )
                  )}
                </ul>

                <Button
                  variant="outlined"
                  component="label"
                  disabled={isSubmittingEndorsement}
                  startIcon={<CloudUploadIcon />}
                  sx={{ mb: 2 }}
                >
                  Upload Supporting Document
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      const validTypes = [
                        "application/pdf",
                        "application/msword",
                        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
                      ];
                      if (file && !validTypes.includes(file.type)) {
                        toast.error("Only PDF or Word documents are allowed.");
                        e.target.value = "";
                        return;
                      }
                      setEndorsementFile(file);
                      toast.success("File selected successfully!");
                    }}
                  />
                </Button>
                {endorsementFile && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                    Selected File: {endorsementFile.name}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Allowed file types: PDF, DOC, DOCX
                </Typography>

                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Contact Information
                </Typography>
                <TextField
                  label=" Name"
                  name="contactName"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  fullWidth
                  required
                  placeholder="Full name"
                  sx={{ mb: 2 }}
                />

                <TextField
                  label=" Email"
                  type="email"
                  name="contactEmail"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  fullWidth
                  required
                  placeholder="name@example.com"
                  sx={{ mb: 1 }}
                />
              </>
            ) : (
              <Typography variant="h6" sx={{ py: 5, textAlign: "center" }}>
                No bond selected.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEndorsementDialogOpen(false)}
              disabled={isSubmittingEndorsement}
            >
              Close
            </Button>
            {selectedBondToEndorse &&
              selectedBondToEndorse.bond_type !== "Blanket" && (
                <>
                  {(role === "internal_user" || role === "super_user") && (
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={!selectedBondToEndorse || isSubmittingEndorsement}
                    onClick={async () => {
                      if (!selectedBondToEndorse) return;
                      const getRiderUrl = () => {
                        switch (selectedBondToEndorse.bond_type) {
                          case "VA":
                            return `/release-rider-va/${selectedBondToEndorse.bond_id}`;
                          case "Trustee":
                            return `/release-rider-trustee/${selectedBondToEndorse.bond_id}`;
                          case "Probate":
                            return `/release-rider-probate/${selectedBondToEndorse.bond_id}`;
                          case "Blanket":
                            return `/release-rider-blanket/${selectedBondToEndorse.bond_id}`;
                          case "Guardianship":
                            return `/release-rider-guardianship/${selectedBondToEndorse.bond_id}`;
                          default:
                            return `/release-rider-guardianship/${selectedBondToEndorse.bond_id}`;
                        }
                      };
                      const isInternalOrSuper = role === "internal_user" || role === "super_user";
                      const countyOk = endorsementType !== "Name_Change_County" || !!endorsementToCounty;
                      const dwbOk = endorsementType !== "Name_Change_Deceased_Ward_Beneficiary" || (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single ? !!updatedName : !!(dwbFirstName || dwbLastName));
                      const nameChangeOk = endorsementType === "Name_Change_County" ? countyOk : (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? dwbOk : (endorsementType === "Name_Change_Principal" ? !!updatedName : true));
                      const hasDetails = endorsementType &&
                        contactName &&
                        contactEmail &&
                        (isNameChangeEndorsement(endorsementType) ? nameChangeOk : (!!newBondAmount && !!endorsementFile));

                      if (isInternalOrSuper && hasDetails) {
                        setIsSubmittingEndorsement(true);
                        const formData = new FormData();
                        formData.append("endorsement_type", endorsementType);
                        if (isNameChangeEndorsement(endorsementType)) {
                          if (endorsementType === "Name_Change_County") {
                            formData.append("updated_name", (endorsementToCounty || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
                          } else if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
                            if (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
                              formData.append("updated_name", updatedName);
                            } else {
                              formData.append("updated_name", [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" "));
                              formData.append("updated_first_name", dwbFirstName);
                              formData.append("updated_middle_name", dwbMiddleName);
                              formData.append("updated_last_name", dwbLastName);
                              formData.append("updated_suffix", dwbSuffix);
                              formData.append("updated_alias", dwbAlias);
                            }
                          } else {
                            formData.append("updated_name", updatedName);
                          }
                        } else {
                          formData.append("updated_bond_amount", newBondAmount.replace(/[^\d.]/g, ""));
                        }
                        if (endorsementFile) formData.append("endorsement_file", endorsementFile);
                        formData.append("contact_name", contactName);
                        formData.append("contact_email", contactEmail);
                        const estAssetsNum = estimatedAssets !== "" && !Number.isNaN(Number(estimatedAssets.replace(/[^\d.]/g, ""))) ? Number(estimatedAssets.replace(/[^\d.]/g, "")) : null;
                        if ((selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && estAssetsNum != null) {
                          formData.append("estimated_assets", estAssetsNum);
                        }
                        try {
                          await apiClient(
                            `/endorse-bond/`,
                            "POST",
                            formData,
                            { bond_id: selectedBondToEndorse.bond_id },
                            { "Content-Type": "multipart/form-data" }
                          );
                          toast.success("Bond endorsed successfully!");
                          setEndorsementDialogOpen(false);
                          await fetchInitialData();
                          navigate(getRiderUrl());
                        } catch (error) {
                          console.error("Endorsement failed:", error);
                          toast.error(error.message || "Failed to endorse the bond.");
                        } finally {
                          setIsSubmittingEndorsement(false);
                        }
                      } else {
                        const url = getRiderUrl();
                        if (url) navigate(url);
                      }
                    }}
                  >
                    Release Rider
                  </Button>
                  )}
                  {role !== "internal_user" && role !== "super_user" && (
                  <Button
                    variant="contained"
                    disabled={
                      isSubmittingEndorsement ||
                      ((!isNameChangeEndorsement(endorsementType) && !endorsementFile)) ||
                      (endorsementType === "Name_Change_County" && !endorsementToCounty) ||
                      (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" && !(DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single ? updatedName : (dwbFirstName || dwbLastName)))
                    }
                    onClick={async () => {
                      const countyOk = endorsementType !== "Name_Change_County" || !!endorsementToCounty;
                      const dwbOk = endorsementType !== "Name_Change_Deceased_Ward_Beneficiary" || (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single ? !!updatedName : !!(dwbFirstName || dwbLastName));
                      const nameChangeOk = endorsementType === "Name_Change_County" ? countyOk : (endorsementType === "Name_Change_Deceased_Ward_Beneficiary" ? dwbOk : (endorsementType === "Name_Change_Principal" ? !!updatedName : true));
                      if (
                        !selectedBondToEndorse ||
                        (!isNameChangeEndorsement(endorsementType) && !endorsementFile) ||
                      (isNameChangeEndorsement(endorsementType) ? !nameChangeOk : !newBondAmount) ||
                !endorsementType ||
                !contactName ||
                !contactEmail
                      ) {
                        toast.error(
                          isNameChangeEndorsement(endorsementType)
                            ? "Please fill all required fields and contact info."
                            : "Please fill all required fields, contact info, and upload the file."
                        );
                        return;
                      }

                      setIsSubmittingEndorsement(true);

                      const formData = new FormData();
                      formData.append("endorsement_type", endorsementType);
                    if (isNameChangeEndorsement(endorsementType)) {
                      if (endorsementType === "Name_Change_County") {
                        formData.append("updated_name", (endorsementToCounty || "").trim().split(/\s+/).filter(Boolean).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(" "));
                      } else if (endorsementType === "Name_Change_Deceased_Ward_Beneficiary") {
                        if (DWB_LABEL_MAP[selectedBondToEndorse?.bond_type]?.single) {
                          formData.append("updated_name", updatedName);
                        } else {
                          formData.append("updated_name", [dwbFirstName, dwbMiddleName, dwbLastName, dwbSuffix].filter(Boolean).join(" "));
                          formData.append("updated_first_name", dwbFirstName);
                          formData.append("updated_middle_name", dwbMiddleName);
                          formData.append("updated_last_name", dwbLastName);
                          formData.append("updated_suffix", dwbSuffix);
                          formData.append("updated_alias", dwbAlias);
                        }
                      } else {
                        formData.append("updated_name", updatedName);
                      }
                    } else {
                      formData.append(
                        "updated_bond_amount",
                        newBondAmount.replace(/[^\d.]/g, "")
                      );
                    }
                      if (endorsementFile) formData.append("endorsement_file", endorsementFile);
              formData.append("contact_name", contactName);
              formData.append("contact_email", contactEmail);
              const estAssetsNum = estimatedAssets !== "" && !Number.isNaN(Number(estimatedAssets.replace(/[^\d.]/g, ""))) ? Number(estimatedAssets.replace(/[^\d.]/g, "")) : null;
              if ((selectedBondToEndorse.bond_type === "Probate" || selectedBondToEndorse.bond_type === "Guardianship") && estAssetsNum != null) {
                formData.append("estimated_assets", estAssetsNum);
              }

                      try {
                        await apiClient(
                          `/endorse-bond/`,
                          "POST",
                          formData,
                          { bond_id: selectedBondToEndorse.bond_id },
                          {
                            "Content-Type": "multipart/form-data",
                          }
                        );

                        toast.success("Bond endorsed successfully!");
                        setEndorsementDialogOpen(false);
                        await fetchInitialData();
                      } catch (error) {
                        console.error("Endorsement failed:", error);
                        toast.error(
                          error.message || "Failed to endorse the bond."
                        );
                      } finally {
                        setIsSubmittingEndorsement(false);
                      }
                    }}
                  >
                    {isSubmittingEndorsement ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Submit Endorsement"
                    )}
                  </Button>
                  )}
                </>
              )}
          </DialogActions>
        </Dialog>
      </Box>
    </>
  );
};

export default ProfessionalGuardianViewBonds;